import { useIonRouter } from '@ionic/react';
import { useQueries } from '@tanstack/react-query';
import {
  type AuthMeResponseDto,
  type ExchangeBusinessCardDto,
  type Lead,
  LeadSource,
  LeadsApi,
  type Profile,
  ProfilesApi,
} from '@up2stay/up2promo-api-client';
import { splitLeadDisplayName } from '@up2stay/up2promo-commons';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  cn,
  Label,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  Textarea,
} from '@up2stay/up2promo-ui';
import { Mail, Phone, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { shellPaths } from '@/core/router/app-paths';

import { LeadDuplicateResolutionDialog } from '@/core/components/leads/lead-duplicate-resolution-dialog';
import { useWorkspaces } from '@/core/hooks/use-workspaces';
import {
  findLeadDuplicates,
  type LeadDuplicateMatch,
} from '@/core/lib/find-lead-duplicates';
import { getAbsoluteFileUrl } from '@/core/lib/file-url';
import {
  getLeadDuplicateConflict,
  type LeadIncomingData,
  replaceExistingLead,
  supplementExistingLead,
} from '@/core/lib/lead-duplicate-actions';
import {
  scanExchangeDebug,
  summarizeExchangeForm,
} from '@/core/lib/scan-exchange-debug';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { useAuthStore } from '@/core/store/auth.store';
import { useSelectedWorkspaceId } from '@/core/store/workspace.store';
import {
  type ContactInfoEntry,
  ContactType,
} from '@/core/types/profile-modules';
import { getApiBusinessErrorCode } from '@/core/utils/api-error.utils';

type ExchangeForm = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName: string;
  jobTitle: string;
  message: string;
};

type ProfileModuleWithConfig = Profile['modules'][number] & {
  config?: { items?: ContactInfoEntry[] };
};

function contactEntries(profile: Profile): ContactInfoEntry[] {
  const module = profile.modules?.find(
    (candidate) =>
      candidate.type === 'contact_info' && candidate.enabled !== false,
  ) as ProfileModuleWithConfig | undefined;
  const items = module?.config?.items;
  return Array.isArray(items) ? items.filter((item) => item.value?.trim()) : [];
}

function displayNameFromProfile(
  profile: Profile,
  user?: AuthMeResponseDto,
): { firstName: string; lastName: string } {
  const title = profile.title?.trim();
  if (title) {
    const { givenName, familyName } = splitLeadDisplayName(title);
    return { firstName: givenName, lastName: familyName };
  }

  if (user) {
    if (user.firstName?.trim() || user.lastName?.trim()) {
      return {
        firstName: user.firstName?.trim() ?? '',
        lastName: user.lastName?.trim() ?? '',
      };
    }
    const username = user.username?.trim();
    if (username) return { firstName: username, lastName: '' };
  }

  return { firstName: '', lastName: '' };
}

function preferredContactValue(
  entries: ContactInfoEntry[],
  kind: ContactInfoEntry['kind'],
): string {
  const matching = entries.filter((entry) => entry.kind === kind);
  const publicBusiness = matching.find(
    (entry) => entry.visible !== false && entry.type !== ContactType.PRIVATE,
  );
  const candidate = publicBusiness ?? matching[0];
  return candidate?.value?.trim() ?? '';
}

function formFromProfile(
  profile: Profile,
  user?: AuthMeResponseDto,
  message = '',
): ExchangeForm {
  const entries = contactEntries(profile);
  const profileEmail = preferredContactValue(entries, 'email');
  const profilePhone = preferredContactValue(entries, 'phone');
  const { firstName, lastName } = displayNameFromProfile(profile, user);

  return {
    firstName,
    lastName,
    email: profileEmail || user?.email?.trim() || '',
    phone: profilePhone || user?.phone?.trim() || '',
    companyName: profile.company?.trim() ?? '',
    jobTitle: profile.jobTitle?.trim() ?? '',
    message,
  };
}

function profileAvatarUrl(
  profile: Profile,
  user?: AuthMeResponseDto,
): string | undefined {
  const path = profile.profileImage?.path?.trim() || user?.photo?.path?.trim();
  return path ? getAbsoluteFileUrl(path) : undefined;
}

function workspaceLogoUrl(workspace: {
  photo?: { path?: string } | null;
}): string | undefined {
  const path = workspace.photo?.path?.trim();
  return path ? getAbsoluteFileUrl(path) : undefined;
}

function initialsFromName(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function incomingForExchange(
  incoming: LeadIncomingData | null,
  selectedProfileId: string,
  workspaceId: string,
  targetProfileTitle?: string,
): LeadIncomingData {
  if (incoming) {
    return {
      ...incoming,
      source: LeadSource.BUSINESS_CARD,
      profileId: selectedProfileId,
      workspaceId,
    };
  }
  const title = targetProfileTitle?.trim() || 'Contact';
  const { givenName, familyName } = splitLeadDisplayName(title);
  return {
    firstName: givenName || title,
    lastName: familyName || undefined,
    source: LeadSource.BUSINESS_CARD,
    profileId: selectedProfileId,
    workspaceId,
  };
}

function matchesFromConflict(
  leads: Lead[],
  incoming: LeadIncomingData,
): LeadDuplicateMatch[] {
  const matches = findLeadDuplicates(leads, incoming);
  if (matches.length > 0) return matches;
  return leads.map((lead) => ({
    lead,
    reasons: ['email'] as LeadDuplicateMatch['reasons'],
  }));
}

export function BusinessCardExchangeDialog({
  open,
  onOpenChange,
  targetProfileId,
  targetProfileTitle,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targetProfileId: string;
  targetProfileTitle?: string;
}) {
  const dictionary = useDictionary();
  const dict = dictionary.businessCardExchange;
  const leadsDict = dictionary.leads;
  const router = useIonRouter();
  const user = useAuthStore((state) => state.user);
  const selectedWorkspaceId = useSelectedWorkspaceId();
  const { workspaces } = useWorkspaces();
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [form, setForm] = useState<ExchangeForm | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [createdLeadId, setCreatedLeadId] = useState<string | null>(null);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicateMatches, setDuplicateMatches] = useState<
    LeadDuplicateMatch[]
  >([]);
  const [pendingIncoming, setPendingIncoming] =
    useState<LeadIncomingData | null>(null);
  const [isResolvingDuplicate, setIsResolvingDuplicate] = useState(false);

  const duplicateDict = leadsDict.duplicate as Record<string, unknown>;
  const duplicateLabels = useMemo(
    () => ({
      title: String(duplicateDict.title),
      description: String(duplicateDict.description),
      selectLead: String(duplicateDict.selectLead),
      matchReason: (duplicateDict.matchReason ?? {}) as Record<
        'email' | 'phone' | 'nameCompany',
        string
      >,
      chooseAction: String(duplicateDict.chooseAction ?? 'What should happen?'),
      useExisting: String(duplicateDict.useExisting ?? 'Use this lead'),
      useExistingDescription: String(
        duplicateDict.useExistingDescription ??
          'Keep the existing contact as it is and continue.',
      ),
      supplement: String(duplicateDict.supplement),
      supplementDescription: String(duplicateDict.supplementDescription),
      replace: String(duplicateDict.replace),
      replaceDescription: String(duplicateDict.replaceDescription),
      createNew: String(duplicateDict.createNew ?? 'Save as new lead'),
      createNewDescription: String(
        duplicateDict.createNewDescription ??
          'Just create a new lead — the existing one stays unchanged.',
      ),
      moreOptions: String(duplicateDict.moreOptions ?? 'Change existing lead…'),
      continue: String(duplicateDict.continue ?? 'Continue'),
      cancel: String(duplicateDict.cancel ?? duplicateDict.discard ?? 'Cancel'),
      discard: String(duplicateDict.discard ?? 'Cancel'),
      processing: String(duplicateDict.processing),
      supplementSuccess: String(duplicateDict.supplementSuccess),
      replaceSuccess: String(duplicateDict.replaceSuccess),
      createNewSuccess: String(
        duplicateDict.createNewSuccess ?? 'Saved as new lead',
      ),
      useExistingSuccess: String(
        duplicateDict.useExistingSuccess ?? 'Using existing lead',
      ),
      actionFailed: String(duplicateDict.actionFailed),
    }),
    [duplicateDict],
  );

  const getStatusLabel = (status: string) =>
    leadsDict.status[status as keyof typeof leadsDict.status] || status;

  const nameInvalid = Boolean(
    validationAttempted && form && !form.firstName.trim(),
  );
  const contactInvalid = Boolean(
    validationAttempted && form && !(form.email.trim() || form.phone.trim()),
  );

  const profileQueries = useQueries({
    queries: workspaces.map((workspace) => ({
      queryKey: ['exchange-profile', user?.id, workspace.id],
      queryFn: async () => {
        if (!user) return null;
        try {
          const response = await ProfilesApi.findByUserAndWorkspace({
            path: { userId: user.id, workspaceId: workspace.id },
          });
          return response.data ?? null;
        } catch (error) {
          const status = (error as { response?: { status?: number } }).response
            ?.status;
          if (status === 404) return null;
          throw error;
        }
      },
      enabled: open && Boolean(user),
      staleTime: 30_000,
    })),
  });

  const profileOptions = useMemo(
    () =>
      profileQueries.flatMap((query, index) => {
        const profile = query.data;
        const workspace = workspaces[index];
        return profile && workspace ? [{ profile, workspace }] : [];
      }),
    [profileQueries, workspaces],
  );
  const profilesLoading = profileQueries.some((query) => query.isLoading);

  const applyProfileSelection = useCallback(
    (profileId: string) => {
      const nextProfile = profileOptions.find(
        ({ profile }) => profile.id === profileId,
      )?.profile;
      if (!nextProfile) {
        scanExchangeDebug('exchange-dialog.profile-selection-missed', {
          profileId,
          optionIds: profileOptions.map(({ profile }) => profile.id),
        });
        return;
      }
      setForm((current) => {
        const nextForm = formFromProfile(
          nextProfile,
          user,
          current?.message ?? '',
        );
        scanExchangeDebug('exchange-dialog.profile-selected', {
          profileId,
          profileTitle: nextProfile.title ?? null,
          workspaceId: profileOptions.find(
            ({ profile }) => profile.id === profileId,
          )?.workspace.id,
          contactModule: (() => {
            const module = nextProfile.modules?.find(
              (candidate) => candidate.type === 'contact_info',
            ) as ProfileModuleWithConfig | undefined;
            return {
              found: Boolean(module),
              enabled: module?.enabled,
              itemCount: Array.isArray(module?.config?.items)
                ? module.config.items.length
                : 0,
              hasConfig: Boolean(module?.config),
            };
          })(),
          form: summarizeExchangeForm(nextForm),
          authFallbacks: {
            hasAccountEmail: Boolean(user?.email?.trim()),
            hasAccountPhone: Boolean(user?.phone?.trim()),
          },
        });
        return nextForm;
      });
      setSelectedProfileId(profileId);
      setValidationAttempted(false);
    },
    [profileOptions, user],
  );

  useEffect(() => {
    scanExchangeDebug('exchange-dialog.state', {
      open,
      targetProfileId,
      targetProfileTitle: targetProfileTitle ?? null,
      profilesLoading,
      profileOptionCount: profileOptions.length,
      profileOptions: profileOptions.map(({ profile, workspace }) => ({
        profileId: profile.id,
        profileTitle: profile.title ?? null,
        workspaceId: workspace.id,
        workspaceName: workspace.name,
      })),
      selectedProfileId: selectedProfileId || null,
      hasForm: Boolean(form),
      form: form ? summarizeExchangeForm(form) : null,
      createdLeadId,
      isSubmitting,
    });
  }, [
    open,
    targetProfileId,
    targetProfileTitle,
    profilesLoading,
    profileOptions,
    selectedProfileId,
    form,
    createdLeadId,
    isSubmitting,
  ]);

  const resetDuplicateState = useCallback(() => {
    setDuplicateOpen(false);
    setDuplicateMatches([]);
    setPendingIncoming(null);
  }, []);

  const [prevOpen, setPrevOpen] = useState(open);
  if (!open && open !== prevOpen) {
    setPrevOpen(open);
    setSelectedProfileId('');
    setForm(null);
    setCreatedLeadId(null);
    setValidationAttempted(false);
    setSubmitError(null);
    resetDuplicateState();
  } else if (open && open !== prevOpen) {
    setPrevOpen(open);
  }

  useEffect(() => {
    if (!open || selectedProfileId || profileOptions.length === 0) return;
    const preferred =
      profileOptions.find(
        ({ workspace }) => workspace.id === selectedWorkspaceId,
      ) ?? profileOptions[0];
    if (!preferred) return;
    scanExchangeDebug('exchange-dialog.auto-select-profile', {
      profileId: preferred.profile.id,
      workspaceId: preferred.workspace.id,
      selectedWorkspaceId,
    });
    queueMicrotask(() => applyProfileSelection(preferred.profile.id));
  }, [
    applyProfileSelection,
    open,
    profileOptions,
    selectedProfileId,
    selectedWorkspaceId,
  ]);

  const buildExchangeBody = (options?: {
    resolvedScannerLeadId?: string;
    allowDuplicateScannerLead?: boolean;
  }): ExchangeBusinessCardDto | null => {
    if (!form || !selectedProfileId) return null;
    const email = form.email.trim();
    const phone = form.phone.trim();
    return {
      scannerProfileId: selectedProfileId,
      targetProfileId,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim() || null,
      email: email || null,
      phone: phone || null,
      companyName: form.companyName.trim() || null,
      jobTitle: form.jobTitle.trim() || null,
      message: form.message.trim() || null,
      consentAccepted: true,
      ...(options?.resolvedScannerLeadId
        ? { resolvedScannerLeadId: options.resolvedScannerLeadId }
        : {}),
      ...(options?.allowDuplicateScannerLead
        ? { allowDuplicateScannerLead: true }
        : {}),
    };
  };

  const scannerWorkspaceIdForSelection = () =>
    profileOptions.find(({ profile }) => profile.id === selectedProfileId)
      ?.workspace.id;

  const completeExchangeSuccess = (leadId?: string) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setValidationAttempted(false);
    setCreatedLeadId(leadId ?? null);
    resetDuplicateState();
  };

  const handleSubmit = async () => {
    if (!form || !selectedProfileId) return;
    setValidationAttempted(true);
    setSubmitError(null);
    const email = form.email.trim();
    const phone = form.phone.trim();
    if (!form.firstName.trim() || (!email && !phone)) {
      toast.error(dict.required);
      return;
    }

    const body = buildExchangeBody();
    if (!body) return;
    const scannerWorkspaceId = scannerWorkspaceIdForSelection();
    if (!scannerWorkspaceId) {
      toast.error(dict.submitError);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await LeadsApi.exchangeBusinessCard({
        path: { workspaceId: scannerWorkspaceId },
        body,
      });
      completeExchangeSuccess(response.data?.scannerLead.id);
    } catch (err: unknown) {
      const conflict = getLeadDuplicateConflict(err);
      if (conflict && conflict.leads.length > 0) {
        const incoming = incomingForExchange(
          conflict.incoming,
          selectedProfileId,
          scannerWorkspaceId,
          targetProfileTitle,
        );
        setPendingIncoming(incoming);
        setDuplicateMatches(matchesFromConflict(conflict.leads, incoming));
        setDuplicateOpen(true);
        return;
      }
      const code = getApiBusinessErrorCode(err);
      const mapped =
        code && dict.errors && code in dict.errors
          ? dict.errors[code as keyof typeof dict.errors]
          : undefined;
      const message = mapped ?? dict.submitError;
      setSubmitError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDuplicateAction = async (
    action: 'use' | 'supplement' | 'replace' | 'create',
    leadId?: string,
  ) => {
    if (!pendingIncoming) return;
    const scannerWorkspaceId = scannerWorkspaceIdForSelection();
    if (!scannerWorkspaceId) {
      toast.error(duplicateLabels.actionFailed);
      return;
    }

    try {
      setIsResolvingDuplicate(true);

      if (action === 'create') {
        const body = buildExchangeBody({ allowDuplicateScannerLead: true });
        if (!body) return;
        const response = await LeadsApi.exchangeBusinessCard({
          path: { workspaceId: scannerWorkspaceId },
          body,
        });
        toast.success(duplicateLabels.createNewSuccess);
        completeExchangeSuccess(response.data?.scannerLead.id);
        return;
      }

      if (!leadId) return;
      const existing = duplicateMatches.find((m) => m.lead.id === leadId)?.lead;
      if (!existing) return;

      if (action === 'supplement') {
        await supplementExistingLead(leadId, existing, pendingIncoming);
      } else if (action === 'replace') {
        await replaceExistingLead(leadId, pendingIncoming, existing);
      }

      const body = buildExchangeBody({ resolvedScannerLeadId: leadId });
      if (!body) return;
      const response = await LeadsApi.exchangeBusinessCard({
        path: { workspaceId: scannerWorkspaceId },
        body,
      });
      toast.success(
        action === 'use'
          ? duplicateLabels.useExistingSuccess
          : action === 'supplement'
            ? duplicateLabels.supplementSuccess
            : duplicateLabels.replaceSuccess,
      );
      completeExchangeSuccess(response.data?.scannerLead.id ?? leadId);
    } catch (error) {
      console.error('Failed to resolve exchange duplicate:', error);
      toast.error(duplicateLabels.actionFailed);
    } finally {
      setIsResolvingDuplicate(false);
    }
  };

  return (
    <>
      <ResponsiveDialog
        open={open}
        onOpenChange={onOpenChange}
        handleOnly={!createdLeadId}
      >
        <ResponsiveDialogContent
          className="flex max-h-[min(90dvh,40rem)] max-w-lg flex-col overflow-hidden"
          fitContent
          scrollable
          keyboardAware
        >
          <ResponsiveDialogHeader className="relative shrink-0 bg-background pr-12">
            <ResponsiveDialogTitle>{dict.title}</ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              {createdLeadId
                ? dict.success
                : targetProfileTitle?.trim()
                  ? dict.exchangingWith.replace(
                      '{name}',
                      targetProfileTitle.trim(),
                    )
                  : dict.description}
            </ResponsiveDialogDescription>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-0 top-0"
              onClick={() => onOpenChange(false)}
              aria-label={dict.close}
            >
              <X className="h-4 w-4" />
            </Button>
          </ResponsiveDialogHeader>

          {createdLeadId ? (
            <div className="flex shrink-0 flex-col gap-2 pt-2 pb-4 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                {dict.close}
              </Button>
              <Button
                type="button"
                onClick={() => {
                  onOpenChange(false);
                  router.push(
                    shellPaths.leadDetail(createdLeadId),
                    'forward',
                    'push',
                  );
                }}
              >
                {dict.viewLead}
              </Button>
            </div>
          ) : profilesLoading ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              {dict.submitting}
            </div>
          ) : profileOptions.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              {dict.noProfile}
            </div>
          ) : !form ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              {dict.submitting}
            </div>
          ) : (
            <div className="flex flex-col">
              <Label id="exchange-profile-label" className="shrink-0 px-1 pb-2">
                {dict.chooseProfile}
              </Label>
              <div
                role="radiogroup"
                aria-labelledby="exchange-profile-label"
                className="flex max-h-[min(42dvh,22rem)] flex-col gap-3 overflow-y-auto overscroll-contain px-1 pb-3"
              >
                {profileOptions.map(({ profile, workspace }) => {
                  const selected = profile.id === selectedProfileId;
                  const preview =
                    selected && form ? form : formFromProfile(profile, user);
                  const displayName =
                    [preview.firstName, preview.lastName]
                      .filter(Boolean)
                      .join(' ')
                      .trim() ||
                    profile.title?.trim() ||
                    workspace.name;
                  const avatarUrl = profileAvatarUrl(profile, user);
                  const workspaceLogo = workspaceLogoUrl(workspace);
                  const workspaceInitials =
                    initialsFromName(workspace.name) ||
                    workspace.name.slice(0, 2).toUpperCase();
                  const roleLine = [preview.jobTitle, preview.companyName]
                    .filter(Boolean)
                    .join(' · ');
                  const cardInvalid =
                    selected && (nameInvalid || contactInvalid);
                  return (
                    <button
                      key={profile.id}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => applyProfileSelection(profile.id)}
                      className={cn(
                        'rounded-lg border p-5 text-left transition-colors',
                        selected
                          ? 'border-primary bg-primary/5 shadow-sm'
                          : 'border-border hover:border-primary/40',
                        cardInvalid ? 'border-destructive' : null,
                      )}
                    >
                      <div className="grid grid-cols-[2.75rem_minmax(0,1fr)] items-start gap-x-3 gap-y-4">
                        <Avatar className="size-11 justify-self-center rounded-lg">
                          {workspaceLogo ? (
                            <AvatarImage
                              src={workspaceLogo}
                              alt={workspace.name}
                            />
                          ) : null}
                          <AvatarFallback className="rounded-lg text-sm font-semibold">
                            {workspaceInitials}
                          </AvatarFallback>
                        </Avatar>
                        <p className="self-center min-w-0 truncate text-lg font-semibold leading-tight">
                          {workspace.name}
                        </p>
                        <Avatar className="size-11 justify-self-center">
                          {avatarUrl ? (
                            <AvatarImage src={avatarUrl} alt={displayName} />
                          ) : null}
                          <AvatarFallback>
                            {initialsFromName(displayName) || workspaceInitials}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 space-y-1">
                          <p className="text-sm font-medium leading-snug">
                            {displayName}
                          </p>
                          {roleLine ? (
                            <p className="text-sm text-muted-foreground">
                              {roleLine}
                            </p>
                          ) : null}
                          {preview.email ? (
                            <p className="flex min-w-0 items-center gap-2 text-sm text-muted-foreground">
                              <Mail className="h-3.5 w-3.5 shrink-0" />
                              <span className="truncate">{preview.email}</span>
                            </p>
                          ) : null}
                          {preview.phone ? (
                            <p className="flex min-w-0 items-center gap-2 text-sm text-muted-foreground">
                              <Phone className="h-3.5 w-3.5 shrink-0" />
                              <span className="truncate">{preview.phone}</span>
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="shrink-0 space-y-3 border-t bg-background px-1 pt-3 pb-4">
                <p className="text-xs text-muted-foreground">
                  {dict.profilePreviewHint}
                </p>
                <div className="space-y-2">
                  <Label htmlFor="exchange-message">{dict.message}</Label>
                  <Textarea
                    id="exchange-message"
                    value={form.message}
                    onChange={(event) =>
                      setForm((current) =>
                        current
                          ? { ...current, message: event.target.value }
                          : current,
                      )
                    }
                  />
                </div>
                {submitError ? (
                  <p className="text-sm text-destructive" role="alert">
                    {submitError}
                  </p>
                ) : null}
                <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    disabled={isSubmitting}
                  >
                    {dict.close}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? dict.submitting : dict.submit}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </ResponsiveDialogContent>
      </ResponsiveDialog>
      <LeadDuplicateResolutionDialog
        open={duplicateOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && !isResolvingDuplicate) {
            resetDuplicateState();
          }
        }}
        matches={duplicateMatches}
        labels={duplicateLabels}
        getStatusLabel={getStatusLabel}
        isProcessing={isResolvingDuplicate}
        onSupplement={(leadId) =>
          void handleDuplicateAction('supplement', leadId)
        }
        onReplace={(leadId) => void handleDuplicateAction('replace', leadId)}
        onUseExisting={(leadId) => void handleDuplicateAction('use', leadId)}
        onCreateNew={() => void handleDuplicateAction('create')}
        onDiscard={resetDuplicateState}
        showUseExisting
        showCreateNew
      />
    </>
  );
}
